import { Check, X } from "lucide-react";

interface PasswordChecklistProps {
  password: string;
  className?: string;
}

// Reusable password-requirement checklist. Ticks update live as the user types.
export const PASSWORD_RULES = [
  { id: "length", label: "At least 8 characters", test: (p: string) => p.length >= 8 },
  { id: "upper", label: "One uppercase letter (A–Z)", test: (p: string) => /[A-Z]/.test(p) },
  { id: "lower", label: "One lowercase letter (a–z)", test: (p: string) => /[a-z]/.test(p) },
  { id: "number", label: "One number (0–9)", test: (p: string) => /[0-9]/.test(p) },
  { id: "special", label: "One special character (!@#$…)", test: (p: string) => /[^A-Za-z0-9]/.test(p) },
];

// Helper other files can import to gate submission.
export function isPasswordValid(password: string): boolean {
  return PASSWORD_RULES.every((r) => r.test(password));
}

export default function PasswordChecklist({ password, className = "" }: PasswordChecklistProps) {
  // Don't show anything until the user starts typing.
  if (!password) return null;

  return (
    <div className={`mt-2 space-y-1.5 ${className}`}>
      {PASSWORD_RULES.map((rule) => {
        const met = rule.test(password);
        return (
          <div key={rule.id} className="flex items-center gap-2 text-xs">
            <span
              className={`flex items-center justify-center w-4 h-4 rounded-full shrink-0 ${
                met ? "bg-green-500/20 text-green-500" : "bg-gray-500/20 text-gray-400"
              }`}
            >
              {met ? <Check size={11} strokeWidth={3} /> : <X size={11} strokeWidth={3} />}
            </span>
            <span className={met ? "text-green-500" : "text-gray-400"}>{rule.label}</span>
          </div>
        );
      })}
    </div>
  );
}
